import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Loader2, RefreshCw, ShieldCheck, Search, Trash2, Bell, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { adminListUsers, adminDeleteUser, adminNotifyUser } from '../lib/api';
import { useConfirmDialog } from '../components/ConfirmDialog';

/**
 * Admin Users report (`/admin/users`). Admin-only. Lists every registered
 * user with Handle, Email, Role and Date Joined. Includes a client-side
 * filter and a manual refresh so admins can triage new signups without
 * reloading the whole page.
 */
const AdminUsers = () => {
  const { user, loading: authLoading } = useAuth();
  const { confirm, dialog: confirmDialog } = useConfirmDialog();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('');
  const [removingId, setRemovingId] = useState(null);
  const [banner, setBanner] = useState(null); // { kind: 'ok'|'err', text }
  const [notifyTarget, setNotifyTarget] = useState(null); // user row
  const [notifyMsg, setNotifyMsg] = useState('');
  const [notifySending, setNotifySending] = useState(false);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const data = await adminListUsers();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (user?.role === 'admin') load(); }, [user]);

  const handleRemove = async (target) => {
    const label = target.handle ? `@${target.handle}` : (target.email || target.name || 'this user');
    const confirmed = await confirm({
      title: `Remove ${label}?`,
      description: 'This soft-deletes the account (they can no longer sign in) and every prayer they authored will disappear from all public feeds. This cannot be undone from the UI.',
      confirmText: 'Remove user',
      cancelText: 'Cancel',
      destructive: true,
    });
    if (!confirmed) return;
    setRemovingId(target.user_id);
    setBanner(null);
    try {
      const res = await adminDeleteUser(target.user_id);
      // Optimistically drop the row so admins get instant feedback.
      setRows((prev) => prev.filter((r) => r.user_id !== target.user_id));
      const n = Number(res?.prayers_deleted) || 0;
      setBanner({
        kind: 'ok',
        text: `Removed ${label}. ${n} prayer${n === 1 ? '' : 's'} soft-deleted.`,
      });
    } catch (e) {
      setBanner({
        kind: 'err',
        text: e?.response?.data?.detail || e.message || 'Failed to remove user',
      });
    } finally {
      setRemovingId(null);
    }
  };

  const handleNotify = async () => {
    if (!notifyTarget || notifyMsg.trim().length < 3) return;
    setNotifySending(true);
    setBanner(null);
    try {
      await adminNotifyUser(notifyTarget.user_id, notifyMsg.trim());
      const label = notifyTarget.handle ? `@${notifyTarget.handle}` : notifyTarget.email;
      setBanner({ kind: 'ok', text: `Notification sent to ${label}.` });
      setNotifyTarget(null);
      setNotifyMsg('');
    } catch (e) {
      setBanner({ kind: 'err', text: e?.response?.data?.detail || e.message || 'Failed to send notification' });
    } finally {
      setNotifySending(false);
    }
  };

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((u) => (
      (u.handle || '').toLowerCase().includes(needle)
      || (u.email || '').toLowerCase().includes(needle)
      || (u.name || '').toLowerCase().includes(needle)
    ));
  }, [rows, filter]);

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return iso; }
  };

  if (authLoading) {
    return <div className="max-w-5xl mx-auto p-8 text-center text-zinc-500"><Loader2 className="animate-spin inline" /> Loading…</div>;
  }
  if (!user) {
    return (
      <div className="max-w-xl mx-auto p-8 text-center">
        <p className="text-zinc-500 mb-4">Please sign in to view this page.</p>
        <Link to="/" className="text-amber-600 hover:underline">Back to home</Link>
      </div>
    );
  }
  if (user.role !== 'admin') {
    return (
      <div className="max-w-xl mx-auto p-8 text-center">
        <h1 className="text-2xl font-bold mb-2">Admin only</h1>
        <p className="text-zinc-500 mb-4">This page is restricted to moderators.</p>
        <Link to="/" className="text-amber-600 hover:underline">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-6">
        <Users size={22} className="text-amber-500" />
        <h1 className="text-2xl font-bold" data-testid="admin-users-title">Admin · Users</h1>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="refresh-users-btn"
          className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </div>

      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="filter-users-input"
            placeholder="Filter by handle, email, or name…"
            className="w-full h-10 pl-9 pr-3 rounded-full border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
        </div>
        <div
          className="text-xs text-zinc-500"
          data-testid="users-count-label"
        >
          {filtered.length} of {rows.length} users
        </div>
      </div>

      {err && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm mb-4" data-testid="admin-users-error">
          {err}
        </div>
      )}

      {banner && (
        <div
          data-testid="admin-users-banner"
          className={`p-3 rounded-lg text-sm mb-4 ${
            banner.kind === 'ok'
              ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
              : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
          }`}
        >
          {banner.text}
        </div>
      )}

      <div className="overflow-x-auto -mx-4 sm:mx-0 rounded-xl border border-zinc-200 dark:border-zinc-800">
        <table className="w-full text-sm min-w-[720px]" data-testid="admin-users-table">
          <thead className="bg-zinc-50 dark:bg-zinc-900/60 text-left">
            <tr className="text-xs uppercase tracking-wide text-zinc-500">
              <th className="px-4 py-3 font-semibold">Handle</th>
              <th className="px-4 py-3 font-semibold">Email</th>
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Gender</th>
              <th className="px-4 py-3 font-semibold">Role</th>
              <th className="px-4 py-3 font-semibold">Date Joined</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-zinc-500">
                  <Loader2 className="animate-spin inline mr-2" size={14} /> Loading users…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-zinc-500" data-testid="admin-users-empty">
                  {filter ? 'No users match your filter.' : 'No users registered yet.'}
                </td>
              </tr>
            ) : (
              filtered.map((u) => (
                <tr
                  key={u.user_id}
                  data-testid={`admin-user-row-${u.user_id}`}
                  className="hover:bg-zinc-50 dark:hover:bg-zinc-900/40"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {u.picture ? (
                        <img src={u.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-200 flex items-center justify-center text-xs font-semibold">
                          {(u.handle || u.name || '?')[0]?.toUpperCase()}
                        </div>
                      )}
                      <span className="font-medium text-zinc-800 dark:text-zinc-100">
                        @{u.handle || '—'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300 break-all">
                    {u.email || '—'}
                  </td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300">
                    {u.name || '—'}
                  </td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-300 capitalize" data-testid={`admin-user-gender-${u.user_id}`}>
                    {u.gender && u.gender !== 'unspecified' ? u.gender : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {u.role === 'admin' ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300">
                        <ShieldCheck size={12} /> Admin
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300">
                        User
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400 tabular-nums">
                    {fmtDate(u.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {(() => {
                      const isSelf = u.user_id === user.user_id;
                      const isAdminRow = u.role === 'admin';
                      const removeDisabled = isSelf || isAdminRow || removingId === u.user_id;
                      const removeTitle = isSelf
                        ? "You can't remove your own account"
                        : isAdminRow
                          ? "Admins can't be removed here"
                          : `Remove @${u.handle || u.email || 'user'}`;
                      const notifyDisabled = isSelf;
                      const notifyTitle = isSelf
                        ? "You can't notify yourself"
                        : `Notify @${u.handle || u.email || 'user'}`;
                      return (
                        <div className="inline-flex items-center gap-1 justify-end">
                          <button
                            type="button"
                            onClick={() => { setNotifyTarget(u); setNotifyMsg(''); }}
                            disabled={notifyDisabled}
                            data-testid={`notify-user-btn-${u.user_id}`}
                            aria-label={notifyTitle}
                            title={notifyTitle}
                            className={`inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors ${
                              notifyDisabled
                                ? 'text-zinc-300 dark:text-zinc-600 cursor-not-allowed'
                                : 'text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30'
                            }`}
                          >
                            <Bell size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemove(u)}
                            disabled={removeDisabled}
                            data-testid={`remove-user-btn-${u.user_id}`}
                            aria-label={removeTitle}
                            title={removeTitle}
                            className={`inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors ${
                              removeDisabled
                                ? 'text-zinc-300 dark:text-zinc-600 cursor-not-allowed'
                                : 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30'
                            }`}
                          >
                            {removingId === u.user_id
                              ? <Loader2 size={16} className="animate-spin" />
                              : <Trash2 size={16} />}
                          </button>
                        </div>
                      );
                    })()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-zinc-500 flex items-center gap-3 flex-wrap">
        <Link
          to="/admin/reports"
          data-testid="admin-reports-link"
          className="underline hover:text-zinc-800 dark:hover:text-zinc-200"
        >
          Go to Admin · Reports
        </Link>
        <Link
          to="/admin/activity"
          data-testid="admin-activity-link"
          className="underline hover:text-zinc-800 dark:hover:text-zinc-200"
        >
          Go to Admin · Activity
        </Link>
      </div>

      {notifyTarget && (
        <div
          data-testid="notify-user-modal"
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setNotifyTarget(null)}
        >
          <div
            className="bg-white dark:bg-zinc-900 rounded-2xl max-w-md w-full p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold inline-flex items-center gap-2">
                <Bell size={18} className="text-amber-500" />
                Notify @{notifyTarget.handle || notifyTarget.email}
              </h2>
              <button
                type="button"
                onClick={() => setNotifyTarget(null)}
                data-testid="notify-close-btn"
                className="text-zinc-500 hover:text-zinc-800 dark:hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
            <p className="text-xs text-zinc-500 mb-3">
              This message appears in the recipient&apos;s notification bell.
            </p>
            <textarea
              value={notifyMsg}
              onChange={(e) => setNotifyMsg(e.target.value)}
              data-testid="notify-message-input"
              rows={5}
              maxLength={1000}
              placeholder="What would you like to tell them?"
              className="w-full p-3 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-vertical"
            />
            <div className="text-xs text-zinc-400 mt-1">{notifyMsg.length}/1000</div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setNotifyTarget(null)}
                data-testid="notify-cancel-btn"
                className="h-9 px-4 rounded-full text-sm border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleNotify}
                disabled={notifySending || notifyMsg.trim().length < 3}
                data-testid="notify-send-btn"
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-semibold"
              >
                {notifySending ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />}
                Send notification
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmDialog}
    </div>
  );
};

export default AdminUsers;
